import { Button } from '@connected/ui/components/button';
import { Sheet, SheetContent, SheetTrigger } from '@connected/ui/components/sheet';
import { useIsFetching, useIsMutating } from '@tanstack/react-query';
import { useStore } from '@tanstack/react-store';
import { motion } from 'motion/react';
import { useEffect, useMemo } from 'react';

import { cn } from '@/lib/utils';
import { CONNECTION_STATUS_ICONS, connectionStateStore } from '@/stores/network-status';
import { setSynchronizationStatus, SYNCHRONIZATION_STATUS_ICONS, syncStatusStore } from '@/stores/sync-status';

export type VerticalAlign = 'top' | 'center' | 'bottom';
export type HorizontalAlign = 'left' | 'center' | 'right';
export type Align = `${VerticalAlign}-${HorizontalAlign}`;

export type SyncStatusProps = {
    align?: Align;
    className?: string;
};

export function SyncStatus({
    align = 'top-right',
    className,
}: SyncStatusProps) {
    const { sheetOpen, status } = useStore(syncStatusStore, state => ({
        sheetOpen: state.sheetOpen,
        status: state.synchronizationStatus,
    }));

    const isFetching = useIsFetching();
    const isMutating = useIsMutating();

    useEffect(() => {
        if (isFetching > 0) {
            setSynchronizationStatus('syncing:down');
        }
        if (isMutating > 0) {
            setSynchronizationStatus('syncing:up');
        }
        if (isFetching === 0 && isMutating === 0) {
            setSynchronizationStatus('up-to-date');
        }
    }, [isFetching, isMutating, status]);

    return (
        <Sheet
            open={sheetOpen}
            // TODO: Reenable this when the sheet is implemented
            // onOpenChange={setSheetState}
        >
            <SheetTrigger
                className={cn('fixed z-30', {
                    'top-2.5': align.startsWith('top'),
                    'bottom-2': align.startsWith('bottom'),
                    'left-2': align.endsWith('left'),
                    'right-2': align.endsWith('right'),
                })}
                asChild
            >
                <Button
                    variant="outline"
                    size="icon"
                    className={cn('bg-background-primary border border-border shadow-md', className)}
                >

                    <ConnectionIcon />
                </Button>
            </SheetTrigger>
            <SheetContent>
            </SheetContent>
        </Sheet>
    );
};

export function ConnectionIcon() {
    const synchronizationStatus = useStore(syncStatusStore, state => state.synchronizationStatus);
    const connectionStatus = useStore(connectionStateStore, state => state.connectionStatus);

    const Icon = useMemo(() => {
        if (connectionStatus === 'offline') {
            return CONNECTION_STATUS_ICONS.offline;
        }
        return SYNCHRONIZATION_STATUS_ICONS[synchronizationStatus];
    }, [synchronizationStatus, connectionStatus]);

    return (
        <motion.div
            key={synchronizationStatus}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.5 }}
        >
            <Icon />
        </motion.div>
    );
}
